(function (browserWindow, factory) {
  var api = factory(browserWindow);

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (browserWindow) {
    browserWindow.GHMRequiredContact = api;
    api.install();
  }
}(typeof window !== 'undefined' ? window : undefined, function (browserWindow) {
  var ATTRIBUTE_KEYS = Object.freeze({ email: 'Contact email', phone: 'Contact phone' });
  var ElementBase = typeof HTMLElement === 'function' ? HTMLElement : function () {};

  function getCore() {
    if (browserWindow && browserWindow.GHMRequiredContactCore) {
      return browserWindow.GHMRequiredContactCore;
    }

    if (typeof require === 'function') {
      return require('./required-contact-core.js');
    }

    throw new Error('Required contact validation is unavailable.');
  }

  function requestJson(fetchFunction, url, options) {
    return fetchFunction(url, options).then(function (response) {
      if (!response || !response.ok) {
        throw new Error('Cart request failed.');
      }

      return response.json().catch(function () {
        throw new Error('Cart response was invalid.');
      });
    });
  }

  function createCartService(options) {
    var runtime = options || {};
    var runtimeWindow = runtime.browserWindow || browserWindow;
    var fetchFunction = runtime.fetch || (runtimeWindow && runtimeWindow.fetch ? runtimeWindow.fetch.bind(runtimeWindow) : undefined);
    var currentCartPromise;

    if (!runtimeWindow || !fetchFunction) {
      throw new Error('Cart service is unavailable.');
    }

    function cartUrl() {
      if (runtimeWindow.Shopify && runtimeWindow.Shopify.routes && runtimeWindow.Shopify.routes.root) {
        return runtimeWindow.Shopify.routes.root + 'cart.js';
      }

      return '/cart.js';
    }

    function readCart(fresh) {
      if (fresh) {
        currentCartPromise = undefined;
      }

      if (!currentCartPromise) {
        currentCartPromise = requestJson(fetchFunction, cartUrl(), { headers: { Accept: 'application/json' } });
      }

      return currentCartPromise;
    }

    function persist(contact) {
      var validation = getCore().validateContact(contact);
      var normalized = validation.normalized;

      if (!validation.valid) {
        return Promise.reject(new Error('Contact details are invalid.'));
      }

      if (!runtimeWindow.routes || !runtimeWindow.routes.cart_update_url) {
        return Promise.reject(new Error('Cart update route is unavailable.'));
      }

      currentCartPromise = undefined;
      return requestJson(fetchFunction, runtimeWindow.routes && runtimeWindow.routes.cart_update_url, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            'Contact email': normalized.email,
            'Contact phone': normalized.phone
          }
        })
      }).then(function () {
        currentCartPromise = undefined;
        return readCart(true);
      }).then(function (cart) {
        var attributes = cart && cart.attributes;

        if (!attributes || attributes[ATTRIBUTE_KEYS.email] !== normalized.email || attributes[ATTRIBUTE_KEYS.phone] !== normalized.phone) {
          throw new Error('Cart contact read-back did not match.');
        }

        dispatchSaved(runtimeWindow, normalized);
        return normalized;
      }).finally(function () {
        currentCartPromise = undefined;
      });
    }

    return {
      getCart: readCart,
      persist: persist
    };
  }

  function dispatchSaved(runtimeWindow, contact) {
    var EventConstructor = runtimeWindow.CustomEvent || (typeof CustomEvent === 'function' ? CustomEvent : undefined);

    if (runtimeWindow.dispatchEvent && EventConstructor) {
      runtimeWindow.dispatchEvent(new EventConstructor('required-contact:saved', { detail: contact }));
    }
  }

  function messageFor(fields, code) {
    var attributeName = 'data-required-contact-' + code.replace(/[A-Z]/g, function (character) {
      return '-' + character.toLowerCase();
    });

    return fields && fields.getAttribute ? fields.getAttribute(attributeName) || '' : '';
  }

  class RequiredContactFields extends ElementBase {
    connectedCallback() {
      var self = this;
      var saveButton = this.querySelector('[data-required-contact-save]');

      if (!this.requiredContactDirtyEventsBound) {
        this.requiredContactDirtyEventsBound = true;
        ['input', 'change'].forEach(function (eventName) {
          self.addEventListener(eventName, function (event) {
            if (event.target && event.target.matches && event.target.matches('[data-required-contact-email], [data-required-contact-phone]')) {
              self.markDirty();
            }
          });
        });
      }

      if (saveButton && !saveButton.dataset.requiredContactBound) {
        saveButton.dataset.requiredContactBound = 'true';
        saveButton.addEventListener('click', function () {
          var service = getDefaultCartService();
          var validation = self.validate();

          self.showErrors(validation.errors);
          if (!validation.valid) return;
          self.setBusy(true);
          service.persist(validation.normalized).then(function (saved) {
            synchronizeFields(saved);
            self.showStatus('');
          }).catch(function () {
            self.showStatus(messageFor(self, 'saveFailed'));
          }).finally(function () {
            self.setBusy(false);
          });
        });
      }
    }

    getValues() {
      var email = this.querySelector('[data-required-contact-email]');
      var phone = this.querySelector('[data-required-contact-phone]');

      return { email: email ? email.value : '', phone: phone ? phone.value : '' };
    }

    setValues(contact) {
      var email = this.querySelector('[data-required-contact-email]');
      var phone = this.querySelector('[data-required-contact-phone]');

      if (email && contact && contact.email != null) email.value = contact.email;
      if (phone && contact && contact.phone != null) phone.value = contact.phone;
      this.markClean();
    }

    isDirty() {
      return Boolean(this.requiredContactDirty);
    }

    markDirty() {
      this.requiredContactDirty = true;
    }

    markClean() {
      this.requiredContactDirty = false;
    }

    validate() {
      return getCore().validateContact(this.getValues());
    }

    showErrors(errors) {
      var coreErrors = getCore().validateContact({ email: '', phone: '' }).errors;
      var emailError = errors && errors.email;
      var phoneError = errors && errors.phone;

      this.updateError('email', emailError === coreErrors.email ? messageFor(this, 'missingEmail') : emailError ? messageFor(this, 'invalidEmail') : '');
      this.updateError('phone', phoneError === coreErrors.phone ? messageFor(this, 'missingPhone') : phoneError ? messageFor(this, 'invalidPhone') : '');
    }

    clearErrors() {
      this.showErrors({ email: '', phone: '' });
    }

    showStatus(message) {
      var status = this.querySelector('[data-required-contact-status]');

      if (!status) return;
      status.textContent = message || '';
      status.hidden = !message;
    }

    setBusy(isBusy) {
      this.querySelectorAll('[data-required-contact-save]').forEach(function (button) {
        button.disabled = isBusy;
        button.setAttribute('aria-busy', String(isBusy));
      });
    }

    updateError(name, message) {
      var input = this.querySelector('[data-required-contact-' + name + ']');
      var error = this.querySelector('[data-required-contact-' + name + '-error]');

      if (input) input.setAttribute('aria-invalid', String(Boolean(message)));
      if (error) {
        error.textContent = message;
        error.hidden = !message;
      }
    }
  }

  class RequiredContactDialog extends ElementBase {
    connectedCallback() {
      var self = this;
      this.nativeDialog = this.querySelector('[data-required-contact-dialog-native]');
      this.fallback = this.querySelector('[data-required-contact-dialog-fallback]');
      this.fallbackPanel = this.querySelector('[data-required-contact-dialog-fallback-panel]');
      this.bindAction('[data-required-contact-dialog-save]', function () { self.save(); });
      this.bindAction('[data-required-contact-dialog-fallback-save]', function () { self.save(); });
      this.bindAction('[data-required-contact-dialog-cancel]', function () { self.close(); });
      this.bindAction('[data-required-contact-dialog-fallback-cancel]', function () { self.close(); });
      if (this.nativeDialog) {
        this.nativeDialog.addEventListener('cancel', function (event) {
          event.preventDefault();
          self.close();
        });
      }
    }

    bindAction(selector, handler) {
      var button = this.querySelector(selector);
      if (button && !button.dataset.requiredContactBound) {
        button.dataset.requiredContactBound = 'true';
        button.addEventListener('click', handler);
      }
    }

    collect(initialContact, returnFocusTarget, preserveStatus) {
      return this.beginCollection(initialContact, returnFocusTarget, preserveStatus, false);
    }

    collectWithFailure(initialContact, returnFocusTarget) {
      return this.beginCollection(initialContact, returnFocusTarget, true, true);
    }

    beginCollection(initialContact, returnFocusTarget, preserveStatus, showFailure) {
      var self = this;
      var fields;

      if (this.resolveCollection) {
        var busyError = new Error('Contact dialog is busy.');
        busyError.requiredContactDialogBusy = true;
        return Promise.reject(busyError);
      }
      this.usingNative = Boolean(this.nativeDialog && typeof this.nativeDialog.showModal === 'function');
      fields = this.activeFields();

      if (fields) {
        fields.setValues(initialContact || {});
        fields.clearErrors();
      }
      this.returnFocusTarget = returnFocusTarget;
      return new Promise(function (resolve, reject) {
        self.resolveCollection = resolve;
        try {
          if (self.usingNative) {
            self.nativeDialog.showModal();
          } else if (self.fallback) {
            self.fallback.hidden = false;
            if (self.fallbackPanel && self.fallbackPanel.focus) self.fallbackPanel.focus();
            else if (fields && fields.focus) fields.focus();
          }
          if (fields && fields.showStatus && (showFailure || !preserveStatus)) fields.showStatus(showFailure ? messageFor(fields, 'saveFailed') : '');
        } catch (error) {
          self.resolveCollection = undefined;
          self.returnFocusTarget = undefined;
          self.usingNative = false;
          if (self.nativeDialog && self.nativeDialog.open && self.nativeDialog.close) self.nativeDialog.close();
          if (self.fallback) self.fallback.hidden = true;
          reject(error);
        }
      });
    }

    activeFields() {
      if (this.usingNative && this.nativeDialog) {
        return this.nativeDialog.querySelector('required-contact-fields');
      }

      return this.fallback ? this.fallback.querySelector('required-contact-fields') : undefined;
    }

    save() {
      var fields = this.activeFields();
      var validation = fields && fields.validate();

      if (!validation || !validation.valid) {
        if (fields) fields.showErrors(validation ? validation.errors : {});
        return;
      }

      this.close(validation.normalized);
    }

    close(result) {
      if (this.nativeDialog && this.nativeDialog.open) this.nativeDialog.close();
      if (this.fallback) this.fallback.hidden = true;
      if (this.resolveCollection) this.resolveCollection(result);
      this.resolveCollection = undefined;
      this.restoreFocus();
    }

    showSaveFailure() {
      var fields = this.activeFields();

      if (fields && fields.showStatus) fields.showStatus(messageFor(fields, 'saveFailed'));
    }

    restoreFocus() {
      if (!this.returnFocusTarget) return;
      if (this.returnFocusTarget.disabled) return;
      if (this.returnFocusTarget.focus) this.returnFocusTarget.focus();
      this.returnFocusTarget = undefined;
    }
  }

  function getDefaultCartService() {
    if (!defaultCartService) defaultCartService = createCartService();
    return defaultCartService;
  }

  function synchronizeFields(contact) {
    if (!browserWindow.document) return;
    browserWindow.document.querySelectorAll('required-contact-fields').forEach(function (fields) {
      fields.setValues(contact);
      fields.clearErrors();
    });
  }

  function pathFor(value, runtimeWindow) {
    try {
      return new URL(value, runtimeWindow.location && runtimeWindow.location.href || 'https://shopify.invalid/');
    } catch (error) {
      return undefined;
    }
  }

  function isSameOrigin(url, runtimeWindow) {
    return !runtimeWindow.location || !runtimeWindow.location.origin || url.origin === runtimeWindow.location.origin;
  }

  function isVisible(element, runtimeWindow) {
    var current = element;

    while (current) {
      if (current.hidden || current.getAttribute && current.getAttribute('aria-hidden') === 'true') return false;
      if (runtimeWindow.getComputedStyle) {
        var style = runtimeWindow.getComputedStyle(current);
        if (style && (style.display === 'none' || style.visibility === 'hidden')) return false;
      }
      current = current.parentElement;
    }

    return !element.getClientRects || element.getClientRects().length > 0;
  }

  function isAddForm(form, runtimeWindow) {
    var url = form && form.action ? pathFor(form.action, runtimeWindow) : undefined;

    return Boolean(url && isSameOrigin(url, runtimeWindow) && (url.pathname === '/cart/add' || url.pathname === '/cart/add.js' || /\/cart\/add(?:\.js)?$/.test(url.pathname)));
  }

  function isCheckoutControl(control, runtimeWindow) {
    var url = control && control.href ? pathFor(control.href, runtimeWindow) : undefined;
    var linkIsVisible = !control || !control.href || (!control.hidden && (!control.getAttribute || control.getAttribute('aria-hidden') !== 'true'));

    return Boolean(control && (control.name === 'checkout' || (linkIsVisible && isVisible(control, runtimeWindow) && url && isSameOrigin(url, runtimeWindow) && url.pathname === '/checkout')));
  }

  function setControlBusy(control, isBusy) {
    if (!control) return function () {};
    var wasDisabled = Boolean(control.disabled);
    var wasBusy = control.getAttribute ? control.getAttribute('aria-busy') : undefined;
    var hadLoading = Boolean(control.classList && control.classList.contains && control.classList.contains('loading'));
    control.disabled = isBusy || wasDisabled;
    if (control.setAttribute) control.setAttribute('aria-busy', String(isBusy));
    if (control.classList && control.classList.add) control.classList.add('loading');

    return function () {
      control.disabled = wasDisabled;
      if (!control.setAttribute) return;
      if (wasBusy == null) control.removeAttribute('aria-busy');
      else control.setAttribute('aria-busy', wasBusy);
      if (control.classList && !hadLoading) control.classList.remove('loading');
    };
  }

  function cancelledError() {
    var error = new Error('Contact collection was cancelled.');
    error.requiredContactCancelled = true;
    return error;
  }

  function validationError() {
    var error = new Error('Contact details are invalid.');
    error.requiredContactValidation = true;
    return error;
  }

  function createCoordinator(options) {
    var runtime = options || {};
    var runtimeWindow = runtime.browserWindow || browserWindow;
    var cartService = runtime.cartService || getDefaultCartService();
    var replayedForms = new WeakSet();
    var replayedControls = new WeakSet();
    var pendingForms = new WeakSet();
    var pendingControls = new WeakSet();

    function getFieldsFromForm(form) {
      if (!form) return undefined;
      if (form.querySelector) {
        var directFields = form.querySelector('required-contact-fields');
        if (directFields && isVisible(directFields, runtimeWindow)) return directFields;
      }
      if (form.closest) {
        var container = form.closest('product-info, product-form, .product, cart-drawer, .cart-drawer, main-cart-footer, .cart__footer');
        if (container && container.querySelector) {
          var nearbyFields = container.querySelector('required-contact-fields');
          if (nearbyFields && isVisible(nearbyFields, runtimeWindow)) return nearbyFields;
        }
      }
      return undefined;
    }

    function findFields(subject, context) {
      if (runtime.findFields) return runtime.findFields(subject, context);
      var form = subject && subject.tagName === 'FORM' ? subject : subject && subject.form || subject && subject.closest && subject.closest('form');
      var fields = getFieldsFromForm(form);

      if (fields || context === 'product' || !runtimeWindow.document || !runtimeWindow.document.querySelectorAll) return fields;
      return Array.prototype.find.call(runtimeWindow.document.querySelectorAll('required-contact-fields'), function (candidate) {
        var candidateContext = candidate.getAttribute && candidate.getAttribute('data-required-contact-context');
        return isVisible(candidate, runtimeWindow) && (candidateContext === 'cart-drawer' || candidateContext === 'cart-page');
      });
    }

    function findDialog() {
      if (runtime.findDialog) return runtime.findDialog();
      return runtimeWindow.document && runtimeWindow.document.querySelector && runtimeWindow.document.querySelector('required-contact-dialog');
    }

    function showFailure(fields, dialog) {
      if (fields && fields.showStatus) fields.showStatus(messageFor(fields, 'saveFailed'));
      if (dialog && dialog.showSaveFailure) dialog.showSaveFailure();
    }

    function acquire(fields, initiator) {
      var validation = fields && fields.validate ? fields.validate() : undefined;

      if (validation) {
        fields.showErrors(validation.errors);
        return validation.valid ? Promise.resolve({ contact: validation.normalized, fields: fields }) : Promise.reject(validationError());
      }

      var dialog = findDialog();
      if (!dialog || !dialog.collect) return Promise.reject(new Error('Contact dialog is unavailable.'));
      return dialog.collect({}, initiator).then(function (contact) {
        if (!contact) throw cancelledError();
        var dialogValidation = getCore().validateContact(contact);
        if (!dialogValidation.valid) throw new Error('Contact details are invalid.');
        return { contact: dialogValidation.normalized, dialog: dialog };
      });
    }

    function persistChoice(contact, dialog, initiator) {
      return cartService.persist(contact).catch(function () {
        if (!dialog || !dialog.collect) throw new Error('Contact details were not saved.');
        return dialog.collectWithFailure(contact, initiator).then(function (retry) {
          if (!retry) throw cancelledError();
          var retryValidation = getCore().validateContact(retry);
          if (!retryValidation.valid) throw new Error('Contact details are invalid.');
          return persistChoice(retryValidation.normalized, dialog, initiator);
        });
      });
    }

    function recoverInitialReadFailure(fields, initiator) {
      var dialog = fields ? undefined : findDialog();

      if (fields) {
        showFailure(fields);
        return Promise.reject(new Error('Cart details are unavailable.'));
      }
      if (!dialog || !dialog.collectWithFailure) return Promise.reject(new Error('Cart details are unavailable.'));
      return dialog.collectWithFailure({}, initiator).then(function (contact) {
        if (!contact) throw cancelledError();
        var validation = getCore().validateContact(contact);
        if (!validation.valid) throw new Error('Contact details are invalid.');
        return persistChoice(validation.normalized, dialog, initiator);
      });
    }

    function recoverReplayFailure(fields, initiator, contact, replay) {
      var dialog;

      if (fields) return Promise.reject(new Error('Purchase replay failed.'));
      dialog = findDialog();
      if (!dialog || !dialog.collectWithFailure) return Promise.reject(new Error('Purchase replay failed.'));
      return dialog.collectWithFailure(contact, initiator).then(function (retry) {
        if (!retry) throw cancelledError();
        var validation = getCore().validateContact(retry);
        if (!validation.valid) throw new Error('Contact details are invalid.');
        return cartService.getCart(true).then(function () {
          return persistChoice(validation.normalized, dialog, initiator);
        }).then(function (saved) {
          try {
            return replay(saved);
          } catch (error) {
            return recoverReplayFailure(undefined, initiator, saved, replay);
          }
        });
      });
    }

    function ensureContact(fields, initiator) {
      return cartService.getCart(true).then(function (cart) {
        var attributes = cart && cart.attributes || {};
        var existing = { email: attributes[ATTRIBUTE_KEYS.email], phone: attributes[ATTRIBUTE_KEYS.phone] };

        if (fields && fields.isDirty && fields.isDirty()) {
          return acquire(fields, initiator).then(function (choice) {
            return persistChoice(choice.contact, choice.dialog, initiator);
          }).then(function (saved) {
            synchronizeFields(saved);
            return saved;
          });
        }
        synchronizeFields(existing);
        if (getCore().isContactComplete(existing)) return existing;
        return acquire(fields, initiator).then(function (choice) {
          return persistChoice(choice.contact, choice.dialog, initiator);
        });
      }, function () {
        return recoverInitialReadFailure(fields, initiator);
      });
    }

    function replayForm(form, submitter, replayed) {
      if (!form || typeof form.requestSubmit !== 'function') throw new Error('Purchase replay is unavailable.');
      replayed.add(form);
      try {
        if (submitter == null) form.requestSubmit();
        else form.requestSubmit(submitter);
      } catch (error) {
        replayed.delete(form);
        throw error;
      }
      replayed.delete(form);
      return true;
    }

    function replayControl(control, form) {
      if (form) {
        replayedControls.add(control);
        try {
          return replayForm(form, control, replayedForms);
        } finally {
          replayedControls.delete(control);
        }
      }
      if (!control || control.disabled || typeof control.click !== 'function') throw new Error('Purchase replay is unavailable.');
      replayedControls.add(control);
      try {
        control.click();
      } catch (error) {
        replayedControls.delete(control);
        throw error;
      }
      replayedControls.delete(control);
      return true;
    }

    function handleAdd(event, form, submitter) {
      if (replayedForms.has(form)) {
        replayedForms.delete(form);
        return Promise.resolve(false);
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      if (pendingForms.has(form)) return Promise.resolve(false);
      pendingForms.add(form);
      var fields = findFields(form, 'product');
      var restore = setControlBusy(submitter, true);
      if (fields && fields.setBusy) fields.setBusy(true);

      return ensureContact(fields, submitter).then(function (contact) {
        if (fields && fields.showStatus) fields.showStatus('');
        restore();
        if (fields && fields.setBusy) fields.setBusy(false);
        try {
          return replayForm(form, submitter, replayedForms);
        } catch (error) {
          return recoverReplayFailure(fields, submitter, contact, function () {
            return replayForm(form, submitter, replayedForms);
          });
        }
      }).catch(function (error) {
        if (!error || (!error.requiredContactCancelled && !error.requiredContactDialogBusy && !error.requiredContactValidation)) showFailure(fields, fields ? undefined : findDialog());
        return false;
      }).finally(function () {
        restore();
        if (fields && fields.setBusy) fields.setBusy(false);
        pendingForms.delete(form);
        var dialog = findDialog();
        if (dialog && dialog.restoreFocus) dialog.restoreFocus();
      });
    }

    function handleCheckout(event, control, form) {
      if (replayedControls.has(control)) {
        replayedControls.delete(control);
        return Promise.resolve(false);
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      if (pendingControls.has(control)) return Promise.resolve(false);
      pendingControls.add(control);
      var fields = findFields(form || control, 'cart');
      var restore = setControlBusy(control, true);
      if (fields && fields.setBusy) fields.setBusy(true);

      return ensureContact(fields, control).then(function (contact) {
        if (fields && fields.showStatus) fields.showStatus('');
        restore();
        if (fields && fields.setBusy) fields.setBusy(false);
        try {
          return replayControl(control, form);
        } catch (error) {
          return recoverReplayFailure(fields, control, contact, function () {
            return replayControl(control, form);
          });
        }
      }).catch(function (error) {
        if (!error || (!error.requiredContactCancelled && !error.requiredContactDialogBusy && !error.requiredContactValidation)) showFailure(fields, fields ? undefined : findDialog());
        return false;
      }).finally(function () {
        restore();
        if (fields && fields.setBusy) fields.setBusy(false);
        pendingControls.delete(control);
        var dialog = findDialog();
        if (dialog && dialog.restoreFocus) dialog.restoreFocus();
      });
    }

    function handleSubmit(event) {
      var form = event.target;
      var submitter = event.submitter;
      if (isAddForm(form, runtimeWindow)) return handleAdd(event, form, submitter);
      if (submitter && isCheckoutControl(submitter, runtimeWindow)) return handleCheckout(event, submitter, form);
      return Promise.resolve(false);
    }

    function handleClick(event) {
      var control = event.target && event.target.closest ? event.target.closest('button, input, a') : event.target;
      if (!isCheckoutControl(control, runtimeWindow)) return Promise.resolve(false);
      return handleCheckout(event, control, control.form);
    }

    return { handleSubmit: handleSubmit, handleClick: handleClick };
  }

  var defaultCartService;
  var installedCoordinator;

  function install() {
    if (!browserWindow || !browserWindow.addEventListener || installedCoordinator) return installedCoordinator;
    installedCoordinator = createCoordinator();
    browserWindow.addEventListener('submit', installedCoordinator.handleSubmit, true);
    browserWindow.addEventListener('click', installedCoordinator.handleClick, true);
    browserWindow.addEventListener('required-contact:saved', function (event) {
      synchronizeFields(event.detail);
    });
    return installedCoordinator;
  }

  function registerElements() {
    if (!browserWindow || !browserWindow.customElements) return;
    if (!browserWindow.customElements.get('required-contact-fields')) browserWindow.customElements.define('required-contact-fields', RequiredContactFields);
    if (!browserWindow.customElements.get('required-contact-dialog')) browserWindow.customElements.define('required-contact-dialog', RequiredContactDialog);
  }

  registerElements();

  return {
    ATTRIBUTE_KEYS: ATTRIBUTE_KEYS,
    RequiredContactFields: RequiredContactFields,
    RequiredContactDialog: RequiredContactDialog,
    createCartService: createCartService,
    createCoordinator: createCoordinator,
    install: install
  };
}));
